import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  Typography,
  Snackbar,
} from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import SudatelLogo from '../../assets/sudatel-logo.jpg';
import ZainLogo from '../../assets/zain-logo.svg';
import { ProductCard } from './ProductCard';
import { RECOMMENDATIONS_API_URL } from '../../utils/constants';
import { addToCart } from './cartSlice';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

interface Provider {
  id: string;
  name: string;
  logo: string;
  plans: Plan[];
}

interface Plan {
  name: string;
  initialFee?: number;
  monthlyFee?: number;
  currencyCode?: string;
  period?: string;
}

interface MagicPlanCreatorProps {
  providers?: Provider[];
  onContinue?: (plan: Plan) => void;
}

const defaultProviders: Provider[] = [
  {
    id: 'sudatel',
    name: 'Sudatel',
    logo: SudatelLogo,
    plans: [
      { name: '4G SIMS', monthlyFee: 24.00 },
      { name: '5G SIMS', monthlyFee: 50.00 },
      { name: 'Broadband', monthlyFee: 40.00 },
      { name: 'Mobile Broadband', monthlyFee: 40.00 },
    ],
  },
  {
    id: 'zain',
    name: 'Zain',
    logo: ZainLogo,
    plans: [
      { name: 'MI Monthly Bundle (8GB)', monthlyFee: 12499.97, currencyCode: 'SDG' },
      { name: 'DC Monthly Bundle (10GB)', monthlyFee: 11000.0, currencyCode: 'SDG' },
      { name: 'MI Monthly Bundle (1GB)', monthlyFee: 3237.47, currencyCode: 'SDG' },
      { name: 'MI Monthly Bundle (3GB)', monthlyFee: 7374.97, currencyCode: 'SDG' },
    ],
  },
];

export const BuildYourOwn: React.FC<MagicPlanCreatorProps> = ({
  providers = defaultProviders,
  onContinue = () => {},
}) => {
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [magicPlans, setMagicPlans] = useState<Plan[]>([]);
  const [creatingMagicPlans, setCreatingMagicPlans] = useState<boolean>(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const dispatch = useDispatch();

  const handleProviderSelect = (provider: Provider) => {
    setSelectedProvider(provider);
    setSelectedPlan(null);
    setMagicPlans([]);
  };

  const createMagicPlans = async (plan: Plan) => {
    if (creatingMagicPlans) return;

    setCreatingMagicPlans(true);
    setSelectedPlan(plan);

    try {
      const response = await fetch(`${RECOMMENDATIONS_API_URL}/queryProductRecommendation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          queryProductRecommendation: {
            instantSyncRecommendation: true,
            description: `${plan.name} at ${plan.monthlyFee}SDG per month`,
            category: "competitor_plan"
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      console.log('Plan Recommendations:', data, JSON.parse(data.recommendationItem));
      setMagicPlans(JSON.parse(data.recommendationItem).plans.map((recommendation: any) => {
        return {
          name: recommendation.plan_name,
          monthlyFee: recommendation.plan_price.price,
          currencyCode: 'SDG'
        }
      }));
    } catch (error) {
      console.error('Error fetching magic plans:', error);
      setMagicPlans([]);
    }
    setCreatingMagicPlans(false);
  };

  const handleAddToCart = (plan: Plan) => {
    dispatch(addToCart({
      id: plan.name,
      name: plan.name,
      price: plan.monthlyFee || 0,
      quantity: 1
    }));
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const renderProviderSelection = () => (
    <div className="mb-12">
      <Typography variant="h4" className="mb-6 pt-3 pb-4 text-center font-bold">
        Select Your Current Provider
      </Typography>
      <Grid container spacing={4} justifyContent="center">
        {providers.map((provider) => (
          <Grid item key={provider.id} xs={12} sm={6} md={4}>
            <Card
              className={`cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                selectedProvider?.id === provider.id
                  ? 'border-4 shadow-xl'
                  : 'hover:shadow-lg'
              }`}
              onClick={() => handleProviderSelect(provider)}
              sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', border: (selectedProvider?.id === provider.id ? '4px solid rgb(0, 29, 61)' : 'none') }}
            >
              <CardMedia
                component="img"
                image={provider.logo}
                alt={provider.name}
                sx={{
                  height: 120,
                  objectFit: 'contain',
                  mb: 2
                }}
              />
              <Typography variant="h6" align="center" className="font-semibold">
                {provider.name}
              </Typography>
              {selectedProvider?.id === provider.id && (
                <Typography variant="body2" align="center" className="mt-2 text-primary">
                  Selected
                </Typography>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );

  const renderPlanCard = (plan: Plan, isMagicPlan: boolean = false) => (
    <ProductCard 
      {...plan} 
      onClick={() => !isMagicPlan && createMagicPlans(plan)} 
      showBuyButton={isMagicPlan} 
      onBuyClick={() => isMagicPlan && handleAddToCart(plan)}
      highlighted={!isMagicPlan && selectedPlan?.name === plan.name} 
    />
  );

  const renderCurrentProviderPlans = () => (
    <div className="mb-8">
      <Typography variant="h5" className="mb-4 pt-4 pb-4 text-center">
        Select Your Current Plan
      </Typography>
      <Grid container spacing={2}>
        {selectedProvider?.plans.map((plan) => (
          <Grid item key={plan.name} xs={12} sm={6} md={3}>
            {renderPlanCard(plan)}
          </Grid>
        ))}
      </Grid>
    </div>
  );

  const renderMagicPlans = () => (
    <div>
      <Typography variant="h5" className="mb-4 pt-4 pb-4 text-center">
        {creatingMagicPlans ? 'Fetching Recommendations...' : 'Recommended Magic Plans'}
      </Typography>
      {creatingMagicPlans ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
        {magicPlans.map((plan) => (
          <Grid item key={plan.name} xs={12} sm={6} md={4}>
            {renderPlanCard(plan, true)}
          </Grid>
          ))}
        </Grid>
      )}
    </div>
  );

  return (
    <div className="p-4">
      <Typography variant="h3" color="primary" className="mb-6 pt-3 pb-4 text-center">
        Create Your Magic Plan
      </Typography>
      {renderProviderSelection()}
      {selectedProvider && renderCurrentProviderPlans()}
      {(magicPlans.length > 0 || (selectedPlan && creatingMagicPlans)) && renderMagicPlans()}
      <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Magic Plan added to cart successfully!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BuildYourOwn;
